import { useEffect, useRef, useState, type FormEvent } from "react";
import { errorText } from "../i18n/serverErrors";
import { api } from "../api/apiClient";
import type { ActiveBatchRow, InventoryBalance, WasteCause } from "../api/types";
import { Modal } from "./Modal";
import { useT } from "../i18n/LanguageProvider";
import { t } from "../i18n/t";
import type { PlainKey } from "../i18n/types";
import { useBoxPickPrompt } from "./shop-floor/useBoxPickPrompt";
import { dateTimeText } from "../i18n/intl";

const defaultInventoryEndpoints = {
  listActiveBatches: (...args: Parameters<typeof api.listActiveBatches>) =>
    api.listActiveBatches(...args),
  logWaste: (...args: Parameters<typeof api.logWaste>) => api.logWaste(...args),
};

export function buildItemOptionsFromStock(rows: InventoryBalance[]) {
  const byItem = new Map<
    string,
    {
      item: InventoryBalance["item"];
      zones: { zone: InventoryBalance["zone"]; qty: number }[];
    }
  >();
  for (const row of rows) {
    const q = parseFloat(row.qtyOnHand);
    if (!Number.isFinite(q) || q <= 0) continue;
    const existing = byItem.get(row.item.id);
    const entry = { zone: row.zone, qty: q };
    if (existing) {
      existing.zones.push(entry);
    } else {
      byItem.set(row.item.id, { item: row.item, zones: [entry] });
    }
  }
  return byItem;
}

/** The enum value is the contract; the key beside it is only how it reads. */
const WASTE_OPTIONS: { value: WasteCause; key: PlainKey }[] = [
  { value: "WILTED_ON_FLOOR", key: "waste.cause.WILTED_ON_FLOOR" },
  { value: "STAFF_ACCIDENT", key: "waste.cause.STAFF_ACCIDENT" },
  { value: "BROKEN_DURING_PREP", key: "waste.cause.BROKEN_DURING_PREP" },
  { value: "CUSTOMER_RETURN", key: "waste.cause.CUSTOMER_RETURN" },
  { value: "OTHER", key: "waste.cause.OTHER" },
];

function formatReceivedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t("waste.dateUnavailable");
  return dateTimeText(date);
}

export function LogWasteModal({
  locationId,
  itemOptionsFromStock,
  onClose,
  onSuccess,
  initialItemId,
  initialItemDisplayName,
  inventoryEndpoints = defaultInventoryEndpoints,
  pinnedZoneId,
}: {
  locationId: string;
  itemOptionsFromStock: Map<
    string,
    {
      item: InventoryBalance["item"];
      zones: { zone: InventoryBalance["zone"]; qty: number }[];
    }
  >;
  onClose: () => void;
  onSuccess: () => void;
  /** When set, item is fixed; user picks batch (FIFO order from API) and quantity. */
  initialItemId?: string;
  initialItemDisplayName?: string;
  /** Override API for shop-floor JWT₃ endpoints (defaults to CRM `api`). */
  inventoryEndpoints?: typeof defaultInventoryEndpoints;
  /** When set, batch list filters to batches in this zone. */
  pinnedZoneId?: string;
}) {
  const { t } = useT();
  const [itemId, setItemId] = useState(() => initialItemId ?? "");
  const itemPickerLocked = Boolean(initialItemId);
  const presetItemTitleSuffix =
    (initialItemId ? itemOptionsFromStock.get(initialItemId)?.item.name : undefined) ??
    initialItemDisplayName;
  const modalTitle =
    itemPickerLocked && presetItemTitleSuffix
      ? t("waste.modalTitleFor", { item: presetItemTitleSuffix })
      : t("waste.modalTitle");
  const [batchId, setBatchId] = useState("");
  const [batches, setBatches] = useState<ActiveBatchRow[]>([]);
  const [quantity, setQuantity] = useState("");
  const [wasteCause, setWasteCause] = useState<WasteCause>("WILTED_ON_FLOOR");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const boxPrompt = useBoxPickPrompt();
  const [formError, setFormError] = useState<string | null>(null);
  /**
   * G-73 (owner request 2026-09-15): the picture of what is being thrown
   * away. Waste is the one action whose honesty nobody can check afterwards —
   * the thing is in the bin — so this is the only evidence that will ever
   * exist. Optional on purpose: a camera that will not open must never stop a
   * florist recording the loss.
   */
  const [photo, setPhoto] = useState<File | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const libraryInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setBatchId("");
    setBatches([]);
    if (!itemId) return;
    void inventoryEndpoints
      .listActiveBatches({
        locationId,
        itemId,
        ...(pinnedZoneId ? { zoneId: pinnedZoneId } : {}),
      })
      .then(setBatches)
      .catch(() => setBatches([]));
  }, [itemId, locationId, inventoryEndpoints, pinnedZoneId]);

  const selectedBatch = batches.find((b) => b.id === batchId);
  const zoneIdForBatch = selectedBatch?.zoneId;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      setFormError(t("waste.qtyPositive"));
      return;
    }
    if (!zoneIdForBatch) {
      setFormError(t("waste.selectBatchError"));
      return;
    }
    const maxRem = selectedBatch ? parseFloat(selectedBatch.remainingQty) : 0;
    if (qty > maxRem) {
      setFormError(t("waste.qtyExceedsBatch", { max: maxRem }));
      return;
    }
    setSubmitting(true);
    try {
      // G-84: a batch that sits in a box asks "Is this from box X?" first.
      await boxPrompt.run((boxAnswers) =>
        inventoryEndpoints.logWaste(
          {
            itemId,
            locationId,
            zoneId: zoneIdForBatch,
            batchId,
            quantity: qty,
            wasteCause,
            notes: notes.trim() || undefined,
            ...(boxAnswers.length ? { boxAnswers } : {}),
          },
          photo,
        ),
      );
      onSuccess();
    } catch (err: unknown) {
      setFormError(errorText(err, "waste.couldNotLog"));
    } finally {
      setSubmitting(false);
    }
  };

  const itemIds = [...itemOptionsFromStock.keys()];
  const canSubmit = itemId && batchId && quantity;

  return (
    <Modal title={modalTitle} onClose={onClose}>
      {boxPrompt.element}
      <form className="modal-form" onSubmit={handleSubmit}>
        {formError ? <div className="alert error full">{formError}</div> : null}
        {!itemPickerLocked ? (
          <div className="field">
            <label htmlFor="wasteItem">{t("waste.itemLabel")}</label>
            <select
              id="wasteItem"
              required
              value={itemId}
              onChange={(e) => setItemId(e.target.value)}
            >
              <option value="">{t("waste.selectItemWithStock")}</option>
              {itemIds.map((id) => {
                const e = itemOptionsFromStock.get(id)!;
                return (
                  <option key={id} value={id}>
                    {e.item.name}
                  </option>
                );
              })}
            </select>
          </div>
        ) : null}
        <div className="field">
          <label htmlFor="wasteBatch">{t("waste.batchLabel")}</label>
          <select
            id="wasteBatch"
            required
            disabled={!itemId || batches.length === 0}
            value={batchId}
            onChange={(e) => setBatchId(e.target.value)}
          >
            <option value="">
              {batches.length === 0 && itemId
                ? t("waste.noActiveBatches")
                : t("waste.selectBatch")}
            </option>
            {batches.map((b) => (
              <option key={b.id} value={b.id}>
                {t("waste.batchOption", {
                  when: formatReceivedAt(b.receivedAt),
                  qty: b.remainingQty,
                })}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="wasteQty">{t("waste.quantityLabel")}</label>
          <input
            id="wasteQty"
            type="number"
            min={0.001}
            step="0.001"
            required
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="wasteCause">{t("waste.causeLabel")}</label>
          <select
            id="wasteCause"
            required
            value={wasteCause}
            onChange={(e) => setWasteCause(e.target.value as WasteCause)}
          >
            {WASTE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(o.key)}
              </option>
            ))}
          </select>
        </div>
        <div className="field full">
          <label htmlFor="wasteNotes">{t("waste.notesLabel")}</label>
          <input
            id="wasteNotes"
            type="text"
            maxLength={300}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <div className="field full">
          <label>{t("waste.photoLabel")}</label>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => {
              setPhoto(e.target.files?.[0] ?? null);
              e.target.value = "";
            }}
          />
          <input
            ref={libraryInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              setPhoto(e.target.files?.[0] ?? null);
              e.target.value = "";
            }}
          />
          <div className="waste-photo-actions">
            <button
              type="button"
              className="button plain"
              disabled={submitting}
              onClick={() => cameraInputRef.current?.click()}
            >
              {t("common.takePhoto")}
            </button>
            <button
              type="button"
              className="button plain"
              disabled={submitting}
              onClick={() => libraryInputRef.current?.click()}
            >
              {t("common.chooseFile")}
            </button>
            {photo ? (
              <button
                type="button"
                className="button plain"
                disabled={submitting}
                onClick={() => setPhoto(null)}
              >
                {t("common.remove")}
              </button>
            ) : null}
          </div>
          <p className="field-hint">
            {photo
              ? t("waste.photoAttached", { name: photo.name })
              : t("waste.photoHint")}
          </p>
        </div>
        <div className="button-row">
          <button className="button" type="submit" disabled={submitting || !canSubmit}>
            {submitting ? t("waste.saving") : t("waste.logWaste")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
